import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { OCC_CLASS_SHORT, FIELD_TYPE_LABELS, REQUIREMENT_LABELS } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Layers, Tag, Tags, FormInput, Rat } from "lucide-react";
import { VestigioConfigPanel } from "@/components/VestigioConfig";
import { PgaConfigPanel } from "@/components/PgaConfig";
import { ProtocolConfigPanel } from "@/components/ProtocolConfig";

const CLASS_OPTIONS = ["cliente", "astral"];

function ItemRow({ item, selected, onSelect, onEdit, onToggle, onDelete, onMove, isFirst, isLast, badge, testPrefix }) {
  return (
    <div onClick={onSelect}
      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
        selected ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"
      } ${!item.active ? "opacity-50" : ""}`}
      data-testid={`${testPrefix}-${item.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.name}</p>
        {badge}
      </div>
      {!item.active && <Badge variant="outline" className="text-[9px] shrink-0">inativo</Badge>}
      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
        <button className="text-muted-foreground hover:text-foreground p-0.5 disabled:opacity-20" disabled={isFirst} onClick={() => onMove(-1)} title="Subir"><ArrowUp size={12} /></button>
        <button className="text-muted-foreground hover:text-foreground p-0.5 disabled:opacity-20" disabled={isLast} onClick={() => onMove(1)} title="Descer"><ArrowDown size={12} /></button>
        <Switch checked={!!item.active} onCheckedChange={onToggle} className="scale-[0.65]" data-testid={`${testPrefix}-toggle-${item.id}`} />
        <button className="text-muted-foreground hover:text-primary p-0.5" onClick={onEdit} title="Editar" data-testid={`${testPrefix}-edit-${item.id}`}><Pencil size={13} /></button>
        <button className="text-muted-foreground hover:text-destructive p-0.5" onClick={onDelete} title="Excluir" data-testid={`${testPrefix}-delete-${item.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export default function OperationalConfig() {
  const [data, setData] = useState({ categories: [], types: [], subtypes: [], fields: [] });
  const [selCat, setSelCat] = useState(null);
  const [selType, setSelType] = useState(null);
  const [catDialog, setCatDialog] = useState(null);
  const [typeDialog, setTypeDialog] = useState(null);
  const [subDialog, setSubDialog] = useState(null);
  const [fieldDialog, setFieldDialog] = useState(null);
  const [species, setSpecies] = useState([]);
  const [speciesDialog, setSpeciesDialog] = useState(null);

  const load = useCallback(() => api.get("/taxonomy/admin").then(({ data }) => setData(data)).catch(() => {}), []);
  const loadSpecies = useCallback(() => api.get("/electric-rooms/species", { params: { include_inactive: true } }).then(({ data }) => setSpecies(data)).catch(() => {}), []);
  useEffect(() => { load(); loadSpecies(); }, [load, loadSpecies]);

  const cats = data.categories;
  const types = data.types.filter((t) => t.category_id === selCat);
  const subtypes = data.subtypes.filter((s) => s.type_id === selType);
  const fields = data.fields.filter((f) => f.category_id === selCat);
  const currentCat = cats.find((c) => c.id === selCat);
  const currentType = types.find((t) => t.id === selType);

  const call = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const callSpecies = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      loadSpecies();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const move = (list, idx, dir, path) => {
    const ids = list.map((i) => i.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    call(() => api.patch(`/taxonomy/${path}/reorder`, { ids }));
  };

  const confirmDelete = (label, fn) => {
    if (window.confirm(`Excluir ${label}? Esta ação não pode ser desfeita.`)) call(fn, `${label} excluído(a)`);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="operational-config-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <SlidersHorizontal size={26} className="text-primary" /> Configuração Operacional
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Categorias, tipos, subtipos e campos dinâmicos totalmente parametrizáveis — refletem automaticamente no ERP e nos aplicativos.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Categorias */}
        <Card className="p-4 border-border bg-card space-y-3" data-testid="categories-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Layers size={15} className="text-primary" /> Categorias ({cats.length})</h3>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setCatDialog({ name: "", classes: ["cliente"] })} data-testid="add-category-btn">
              <Plus size={13} className="mr-1" /> Adicionar
            </Button>
          </div>
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto scrollbar-thin pr-1">
            {cats.map((c, i) => (
              <ItemRow key={c.id} item={c} selected={selCat === c.id} testPrefix="category"
                onSelect={() => { setSelCat(c.id); setSelType(null); }}
                onEdit={() => setCatDialog({ ...c })}
                onToggle={() => call(() => api.patch(`/taxonomy/categories/${c.id}/toggle`))}
                onDelete={() => confirmDelete(`categoria "${c.name}"`, () => api.delete(`/taxonomy/categories/${c.id}`))}
                onMove={(dir) => move(cats, i, dir, "categories")}
                isFirst={i === 0} isLast={i === cats.length - 1}
                badge={
                  <span className="flex gap-1 mt-0.5">
                    {(c.classes || ["cliente"]).map((cl) => (
                      <span key={cl} className={`text-[9px] px-1 rounded border ${cl === "astral" ? "border-fuchsia-500/40 text-fuchsia-400" : "border-sky-500/40 text-sky-400"}`}>{OCC_CLASS_SHORT[cl]}</span>
                    ))}
                    {c.usage > 0 && <span className="text-[9px] text-muted-foreground">{c.usage} ocorr.</span>}
                  </span>
                } />
            ))}
          </div>
        </Card>

        {/* Tipos */}
        <Card className="p-4 border-border bg-card space-y-3" data-testid="types-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Tag size={15} className="text-emerald-400" /> Tipos {currentCat ? `· ${currentCat.name}` : ""}</h3>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!selCat}
              onClick={() => setTypeDialog({ name: "", category_id: selCat })} data-testid="add-type-btn">
              <Plus size={13} className="mr-1" /> Adicionar
            </Button>
          </div>
          {!selCat ? (
            <p className="text-xs text-muted-foreground py-10 text-center">Selecione uma categoria à esquerda.</p>
          ) : (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto scrollbar-thin pr-1">
              {types.length === 0 && <p className="text-xs text-muted-foreground py-6 text-center">Nenhum tipo cadastrado.</p>}
              {types.map((t, i) => (
                <ItemRow key={t.id} item={t} selected={selType === t.id} testPrefix="type"
                  onSelect={() => setSelType(t.id)}
                  onEdit={() => setTypeDialog({ ...t })}
                  onToggle={() => call(() => api.patch(`/taxonomy/types/${t.id}/toggle`))}
                  onDelete={() => confirmDelete(`tipo "${t.name}"`, () => api.delete(`/taxonomy/types/${t.id}`))}
                  onMove={(dir) => move(types, i, dir, "types")}
                  isFirst={i === 0} isLast={i === types.length - 1}
                  badge={<span className="text-[9px] text-muted-foreground">{data.subtypes.filter((s) => s.type_id === t.id).length} subtipo(s)</span>} />
              ))}
            </div>
          )}
        </Card>

        {/* Subtipos */}
        <Card className="p-4 border-border bg-card space-y-3" data-testid="subtypes-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Tags size={15} className="text-amber-400" /> Subtipos {currentType ? `· ${currentType.name}` : ""}</h3>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!selType}
              onClick={() => setSubDialog({ name: "", type_id: selType })} data-testid="add-subtype-btn">
              <Plus size={13} className="mr-1" /> Adicionar
            </Button>
          </div>
          {!selType ? (
            <p className="text-xs text-muted-foreground py-10 text-center">Selecione um tipo no painel central.</p>
          ) : (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto scrollbar-thin pr-1">
              {subtypes.length === 0 && <p className="text-xs text-muted-foreground py-6 text-center">Nenhum subtipo cadastrado.</p>}
              {subtypes.map((s, i) => (
                <ItemRow key={s.id} item={s} selected={false} testPrefix="subtype"
                  onSelect={() => {}}
                  onEdit={() => setSubDialog({ ...s })}
                  onToggle={() => call(() => api.patch(`/taxonomy/subtypes/${s.id}/toggle`))}
                  onDelete={() => confirmDelete(`subtipo "${s.name}"`, () => api.delete(`/taxonomy/subtypes/${s.id}`))}
                  onMove={() => {}}
                  isFirst isLast badge={null} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Campos Dinâmicos */}
      <Card className="p-5 border-border bg-card space-y-4" data-testid="fields-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
            <FormInput size={15} className="text-violet-400" /> Campos Dinâmicos {currentCat ? `· ${currentCat.name}` : ""}
          </h3>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!selCat}
            onClick={() => setFieldDialog({ category_id: selCat, label: "", field_type: "texto", options: [], requirement: "opcional" })}
            data-testid="add-field-btn">
            <Plus size={13} className="mr-1" /> Adicionar Campo
          </Button>
        </div>
        {!selCat ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Selecione uma categoria para gerenciar seus campos personalizados (ex.: Quantidade, Número do dispositivo, Necessita coleta).</p>
        ) : fields.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Nenhum campo dinâmico. Os campos criados aqui aparecem automaticamente no registro de ocorrências desta categoria.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 pr-3 font-medium">Campo</th>
                <th className="py-2 pr-3 font-medium">Tipo</th>
                <th className="py-2 pr-3 font-medium">Obrigatoriedade</th>
                <th className="py-2 pr-3 font-medium hidden md:table-cell">Opções</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => (
                <tr key={f.id} className={`border-b border-border/50 last:border-0 ${!f.active ? "opacity-50" : ""}`} data-testid={`field-row-${f.key}`}>
                  <td className="py-2 pr-3 font-medium">{f.label} {!f.active && <Badge variant="outline" className="text-[9px] ml-1">inativo</Badge>}</td>
                  <td className="py-2 pr-3"><Badge variant="outline" className="text-[10px]">{FIELD_TYPE_LABELS[f.field_type]}</Badge></td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{REQUIREMENT_LABELS[f.requirement]}</td>
                  <td className="py-2 pr-3 hidden md:table-cell text-xs text-muted-foreground truncate max-w-[220px]">{f.options?.join(", ") || "—"}</td>
                  <td className="py-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Switch checked={!!f.active} onCheckedChange={() => call(() => api.patch(`/taxonomy/fields/${f.id}/toggle`))} className="scale-[0.7]" />
                      <button className="text-muted-foreground hover:text-primary" onClick={() => setFieldDialog({ ...f })}><Pencil size={13} /></button>
                      <button className="text-muted-foreground hover:text-destructive" onClick={() => confirmDelete(`campo "${f.label}"`, () => api.delete(`/taxonomy/fields/${f.id}`))}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Espécies de Roedores */}
      <Card className="p-5 border-border bg-card space-y-4" data-testid="species-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
              <Rat size={15} className="text-rose-400" /> Espécies de Roedores ({species.length})
            </h3>
            <p className="text-xs text-muted-foreground mt-1">Espécies disponíveis no registro de capturas das Salas Elétricas. Desativar oculta a espécie sem perder o histórico.</p>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => setSpeciesDialog({ name: "", scientific_name: "" })} data-testid="add-species-btn">
            <Plus size={13} className="mr-1" /> Adicionar Espécie
          </Button>
        </div>
        {species.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Nenhuma espécie cadastrada.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {species.map((s) => (
              <div key={s.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card ${!s.active ? "opacity-50" : ""}`} data-testid={`species-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{s.name}</p>
                  {s.scientific_name && <p className="text-[10px] italic text-muted-foreground truncate">{s.scientific_name}</p>}
                </div>
                {!s.active && <Badge variant="outline" className="text-[9px] shrink-0">inativa</Badge>}
                <div className="flex items-center gap-1 shrink-0">
                  <Switch checked={!!s.active} onCheckedChange={() => callSpecies(() => api.patch(`/electric-rooms/species/${s.id}/toggle`))} className="scale-[0.65]" data-testid={`species-toggle-${s.id}`} />
                  <button className="text-muted-foreground hover:text-primary p-0.5" onClick={() => setSpeciesDialog({ ...s })} title="Editar" data-testid={`species-edit-${s.id}`}><Pencil size={13} /></button>
                  <button className="text-muted-foreground hover:text-destructive p-0.5" title="Excluir" data-testid={`species-delete-${s.id}`}
                    onClick={() => { if (window.confirm(`Excluir a espécie "${s.name}"?`)) callSpecies(() => api.delete(`/electric-rooms/species/${s.id}`), "Espécie excluída"); }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Protocolos e código de rastreio */}
      <ProtocolConfigPanel categories={cats} types={data.types} onChanged={load} />

      {/* Vestígios de Salas Elétricas */}
      <VestigioConfigPanel />

      {/* Módulo PGA */}
      <PgaConfigPanel />

      {/* Dialog Espécie */}
      <Dialog open={!!speciesDialog} onOpenChange={(o) => !o && setSpeciesDialog(null)}>
        <DialogContent data-testid="species-dialog">
          <DialogHeader><DialogTitle className="font-heading">{speciesDialog?.id ? "Editar Espécie" : "Nova Espécie de Roedor"}</DialogTitle></DialogHeader>
          {speciesDialog && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nome popular</Label>
                <Input value={speciesDialog.name} placeholder="Ex.: Rato de telhado"
                  onChange={(e) => setSpeciesDialog({ ...speciesDialog, name: e.target.value })} data-testid="species-name-input" /></div>
              <div className="space-y-1.5"><Label>Nome científico (opcional)</Label>
                <Input value={speciesDialog.scientific_name} placeholder="Ex.: Rattus rattus"
                  onChange={(e) => setSpeciesDialog({ ...speciesDialog, scientific_name: e.target.value })} data-testid="species-scientific-input" /></div>
              <Button className="w-full" data-testid="species-save-btn" onClick={() => {
                if (!speciesDialog.name.trim()) return toast.error("Nome da espécie obrigatório");
                const body = { name: speciesDialog.name, scientific_name: speciesDialog.scientific_name || "" };
                callSpecies(() => speciesDialog.id ? api.put(`/electric-rooms/species/${speciesDialog.id}`, body) : api.post("/electric-rooms/species", body),
                  speciesDialog.id ? "Espécie atualizada" : "Espécie cadastrada");
                setSpeciesDialog(null);
              }}>Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Categoria */}
      <Dialog open={!!catDialog} onOpenChange={(o) => !o && setCatDialog(null)}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-heading">{catDialog?.id ? "Editar Categoria" : "Nova Categoria"}</DialogTitle></DialogHeader>
          {catDialog && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nome</Label>
                <Input value={catDialog.name} onChange={(e) => setCatDialog({ ...catDialog, name: e.target.value })} data-testid="category-name-input" /></div>
              <div className="space-y-2">
                <Label>Disponível para</Label>
                <div className="flex gap-4">
                  {CLASS_OPTIONS.map((cl) => (
                    <label key={cl} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={(catDialog.classes || []).includes(cl)}
                        onCheckedChange={(v) => setCatDialog({ ...catDialog, classes: v ? [...(catDialog.classes || []), cl] : (catDialog.classes || []).filter((x) => x !== cl) })}
                        data-testid={`category-class-${cl}`} />
                      Ocorrência {OCC_CLASS_SHORT[cl]}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5"><Label>Código de rastreio (prefixo do protocolo)</Label>
                <Input value={catDialog.protocol_prefix || ""} maxLength={8} placeholder="Ex.: EST, NC, ELT"
                  className="uppercase font-mono"
                  onChange={(e) => setCatDialog({ ...catDialog, protocol_prefix: e.target.value.toUpperCase() })}
                  data-testid="category-prefix-input" />
                <p className="text-[11px] text-muted-foreground">Deixe vazio para o sistema sugerir automaticamente pelo nome.</p>
              </div>
              <Button className="w-full" data-testid="category-save-btn" onClick={() => {
                if (!catDialog.name.trim()) return toast.error("Nome obrigatório");
                const body = { name: catDialog.name, classes: catDialog.classes || ["cliente"], protocol_prefix: catDialog.protocol_prefix || "" };
                call(() => catDialog.id ? api.put(`/taxonomy/categories/${catDialog.id}`, body) : api.post("/taxonomy/categories", body),
                  catDialog.id ? "Categoria atualizada" : "Categoria criada");
                setCatDialog(null);
              }}>Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Tipo */}
      <Dialog open={!!typeDialog} onOpenChange={(o) => !o && setTypeDialog(null)}>
        <DialogContent data-testid="type-dialog">
          <DialogHeader><DialogTitle className="font-heading">{typeDialog?.id ? "Editar Tipo" : "Novo Tipo"}</DialogTitle></DialogHeader>
          {typeDialog && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nome</Label>
                <Input value={typeDialog.name} onChange={(e) => setTypeDialog({ ...typeDialog, name: e.target.value })} data-testid="type-name-input" /></div>
              <Button className="w-full" data-testid="type-save-btn" onClick={() => {
                if (!typeDialog.name.trim()) return toast.error("Nome obrigatório");
                call(() => typeDialog.id ? api.put(`/taxonomy/types/${typeDialog.id}`, { name: typeDialog.name })
                  : api.post("/taxonomy/types", { category_id: typeDialog.category_id, name: typeDialog.name }),
                  typeDialog.id ? "Tipo atualizado" : "Tipo criado");
                setTypeDialog(null);
              }}>Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Subtipo */}
      <Dialog open={!!subDialog} onOpenChange={(o) => !o && setSubDialog(null)}>
        <DialogContent data-testid="subtype-dialog">
          <DialogHeader><DialogTitle className="font-heading">{subDialog?.id ? "Editar Subtipo" : "Novo Subtipo"}</DialogTitle></DialogHeader>
          {subDialog && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nome</Label>
                <Input value={subDialog.name} onChange={(e) => setSubDialog({ ...subDialog, name: e.target.value })} data-testid="subtype-name-input" /></div>
              <Button className="w-full" data-testid="subtype-save-btn" onClick={() => {
                if (!subDialog.name.trim()) return toast.error("Nome obrigatório");
                call(() => subDialog.id ? api.put(`/taxonomy/subtypes/${subDialog.id}`, { name: subDialog.name })
                  : api.post("/taxonomy/subtypes", { type_id: subDialog.type_id, name: subDialog.name }),
                  subDialog.id ? "Subtipo atualizado" : "Subtipo criado");
                setSubDialog(null);
              }}>Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Campo */}
      <Dialog open={!!fieldDialog} onOpenChange={(o) => !o && setFieldDialog(null)}>
        <DialogContent data-testid="field-dialog">
          <DialogHeader><DialogTitle className="font-heading">{fieldDialog?.id ? "Editar Campo Dinâmico" : "Novo Campo Dinâmico"}</DialogTitle></DialogHeader>
          {fieldDialog && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nome do campo</Label>
                <Input value={fieldDialog.label} placeholder="Ex.: Quantidade, Número do dispositivo..."
                  onChange={(e) => setFieldDialog({ ...fieldDialog, label: e.target.value })} data-testid="field-label-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Tipo</Label>
                  <Select value={fieldDialog.field_type} onValueChange={(v) => setFieldDialog({ ...fieldDialog, field_type: v })}>
                    <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Obrigatoriedade</Label>
                  <Select value={fieldDialog.requirement} onValueChange={(v) => setFieldDialog({ ...fieldDialog, requirement: v })}>
                    <SelectTrigger data-testid="field-requirement-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(REQUIREMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {fieldDialog.field_type === "selecao" && (
                <div className="space-y-1.5"><Label>Opções (separadas por vírgula)</Label>
                  <Input value={(fieldDialog.options || []).join(", ")}
                    onChange={(e) => setFieldDialog({ ...fieldDialog, options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    placeholder="Sim, Não, Parcial" data-testid="field-options-input" /></div>
              )}
              <Button className="w-full" data-testid="field-save-btn" onClick={() => {
                if (!fieldDialog.label.trim()) return toast.error("Nome do campo obrigatório");
                const body = { category_id: fieldDialog.category_id, label: fieldDialog.label, field_type: fieldDialog.field_type,
                  options: fieldDialog.options || [], requirement: fieldDialog.requirement };
                call(() => fieldDialog.id ? api.put(`/taxonomy/fields/${fieldDialog.id}`, body) : api.post("/taxonomy/fields", body),
                  fieldDialog.id ? "Campo atualizado" : "Campo criado");
                setFieldDialog(null);
              }}>Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
